import { useEffect, useRef, useState } from 'react';

import { ToastService } from '../../../services/toast.service';
import { type SelectOption } from '../components/SearchableSelect';
import { type CopSubscriptionConfig } from '../CopSubscription.config';
import {
    addSubscription,
    type Country,
    type Customer,
    type CustomerSite,
    type CustomerUser,
    fetchCountries,
    fetchCustomers,
    fetchCustomerSites,
    fetchCustomerUsers,
    fetchSubscriptionById,
    type SubscriptionPayload,
    updateSubscription,
} from '../service/subscription.service';
import {
    DEFAULT_MESSAGES,
    DEFAULT_SUBSCRIPTION_TYPES,
    getSubmitErrorMsg,
    getSubmitSuccessMsg,
    NOSLA_VALUE,
} from '../SubscriptionForm.constants';

export function useSubscriptionForm(config: CopSubscriptionConfig, subscriptionId?: string) {
    const pendingEditSelectionsRef = useRef<{ userIds: string[]; siteIds: string[] } | null>(null);
    const initialEditValuesRef = useRef<{
        customerId: string; type: string; country: string;
        userIds: string[]; siteIds: string[];
    } | null>(null);

    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);
    const [customerUsers, setCustomerUsers] = useState<CustomerUser[]>([]);
    const [sites, setSites] = useState<CustomerSite[]>([]);

    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [searchingCustomers, setSearchingCustomers] = useState(false);
    const [loadingMoreCustomers, setLoadingMoreCustomers] = useState(false);
    const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
    const customersLoadedRef = useRef(false);
    const customerApiCountRef = useRef(0);

    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);

    const [loadingInitial, setLoadingInitial] = useState(false);
    const [loadingDependent, setLoadingDependent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const errTitle = config.confirmation?.errorTitle;
    const errDesc = (fallback: string) => config.confirmation?.errorDescription ?? fallback;

    useEffect(() => {
        const isEdit = config.actionType?.toLowerCase() === 'edit';
        if (isEdit && !subscriptionId) {
            ToastService.error(errDesc(DEFAULT_MESSAGES.loadEditError), errTitle);
            return;
        }
        setLoadingInitial(true);
        if (isEdit && subscriptionId) {
            fetchSubscriptionById(subscriptionId)
                .then((sub) => {
                    if (!sub) { ToastService.error(errDesc(DEFAULT_MESSAGES.loadEditError), errTitle); return; }
                    pendingEditSelectionsRef.current = {
                        userIds: sub.customerUserIds.map(String),
                        siteIds: sub.siteIds,
                    };
                    initialEditValuesRef.current = {
                        customerId: sub.customerId,
                        type: sub.type,
                        country: sub.country,
                        userIds: sub.customerUserIds.map(String),
                        siteIds: sub.siteIds,
                    };
                    return Promise.all([fetchCustomers(undefined, 0), fetchCountries()])
                        .then(([cl, col]) => {
                            // ensure the preselected customer shows up even if it's not on the first fetched page
                            const hasCustomer = cl.some((c) => c.id === sub.customerId);
                            const customersList = hasCustomer
                                ? cl
                                : [{ id: sub.customerId, name: sub.customerName || sub.customerId }, ...cl];
                            setCustomers(customersList); setCountries(col);
                            customerApiCountRef.current = cl.length;
                            setHasMoreCustomers(cl.length === 50);
                            customersLoadedRef.current = true;
                            setSelectedCustomerId(sub.customerId);
                            setSelectedType(sub.type);
                            setSelectedCountry(sub.country);
                        });
                })
                .catch(() => ToastService.error(errDesc(DEFAULT_MESSAGES.loadEditError), errTitle))
                .finally(() => setLoadingInitial(false));
        } else {
            Promise.all([fetchCustomers(undefined, 0), fetchCountries()])
                .then(([cl, col]) => {
                    setCustomers(cl); setCountries(col);
                    customerApiCountRef.current = cl.length;
                    setHasMoreCustomers(cl.length === 50);
                    customersLoadedRef.current = true;
                })
                .catch(() => ToastService.error(errDesc(DEFAULT_MESSAGES.loadInitialError), errTitle))
                .finally(() => setLoadingInitial(false));
        }
    }, []);

    useEffect(() => {
        if (!customersLoadedRef.current) return;
        let cancelled = false;
        const timer = setTimeout(async () => {
            setSearchingCustomers(true);
            try {
                const cl = await fetchCustomers(customerSearchTerm || undefined, 0);
                if (cancelled) return;
                setCustomers(cl);
                customerApiCountRef.current = cl.length;
                setHasMoreCustomers(cl.length === 50);
            } finally {
                if (!cancelled) setSearchingCustomers(false);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [customerSearchTerm]);

    const loadMoreCustomers = async () => {
        if (!hasMoreCustomers || loadingMoreCustomers || searchingCustomers) return;
        setLoadingMoreCustomers(true);
        try {
            const cl = await fetchCustomers(customerSearchTerm || undefined, customerApiCountRef.current);
            setCustomers(prev => [...prev, ...cl]);
            customerApiCountRef.current += cl.length;
            setHasMoreCustomers(cl.length === 50);
        } finally {
            setLoadingMoreCustomers(false);
        }
    };

    useEffect(() => {
        if (!selectedCustomerId || selectedCustomerId === NOSLA_VALUE) {
            setCustomerUsers([]); setSites([]);
            setSelectedUserIds([]); setSelectedSiteIds([]);
            return;
        }
        setLoadingDependent(true);
        Promise.all([fetchCustomerUsers(selectedCustomerId), fetchCustomerSites(selectedCustomerId)])
            .then(([ul, sl]) => {
                setCustomerUsers(ul); setSites(sl);
                if (pendingEditSelectionsRef.current) {
                    setSelectedUserIds(pendingEditSelectionsRef.current.userIds);
                    setSelectedSiteIds(pendingEditSelectionsRef.current.siteIds);
                    pendingEditSelectionsRef.current = null;
                } else {
                    setSelectedUserIds([]); setSelectedSiteIds([]);
                }
            })
            .catch(() => ToastService.error(errDesc(DEFAULT_MESSAGES.loadCustomerDataError), errTitle))
            .finally(() => setLoadingDependent(false));
    }, [selectedCustomerId]);

    const subscriptionTypeOptions: SelectOption[] = (config.subscriptionTypes ?? DEFAULT_SUBSCRIPTION_TYPES)
        .map((s) => ({ value: s.type, label: s.type }));

    const customerOptions: SelectOption[] = customers
        .filter((c) => {
            const id = (c.id ?? '').replace(/\s/g, '').toUpperCase();
            const name = (c.name ?? '').replace(/\s/g, '').toUpperCase();
            return id !== 'NOSLA' && name !== 'NOSLA';
        })
        .map((c) => ({ value: c.id, label: `${c.name} (${c.id})` }));

    const countryOptions = countries.map((c): SelectOption => ({ value: c.name, label: c.name }));
    const userOptions = customerUsers.map((u): SelectOption => ({ value: String(u.id), label: u.email }));
    const siteOptions = sites.map((s): SelectOption => ({ value: s.id, label: s.id }));

    const isCustomerInvalid = submitted && !selectedCustomerId;
    const isTypeInvalid = submitted && !selectedType;
    const isCountryInvalid = submitted && !selectedCountry;
    const isEmailInvalid = submitted && selectedUserIds.length === 0 && selectedCustomerId !== NOSLA_VALUE;
    const isSiteInvalid = submitted && selectedSiteIds.length === 0 && selectedCustomerId !== NOSLA_VALUE;

    const customerSelected = !!selectedCustomerId && selectedCustomerId !== NOSLA_VALUE;
    const showNoUsersMsg = customerSelected && !loadingDependent && customerUsers.length === 0;
    const showNoSitesMsg = customerSelected && !loadingDependent && sites.length === 0;

    const handleCancelClick = () => {
        const isEdit = config.actionType?.toLowerCase() === 'edit';
        let dirty: boolean;
        if (isEdit) {
            const init = initialEditValuesRef.current;
            dirty = !init || (
                selectedCustomerId !== init.customerId ||
                selectedType !== init.type ||
                selectedCountry !== init.country ||
                selectedUserIds.length !== init.userIds.length ||
                !selectedUserIds.every(id => init.userIds.includes(id)) ||
                selectedSiteIds.length !== init.siteIds.length ||
                !selectedSiteIds.every(id => init.siteIds.includes(id))
            );
        } else {
            dirty = !!(selectedCustomerId || selectedType || selectedCountry ||
                selectedUserIds.length > 0 || selectedSiteIds.length > 0);
        }
        if (dirty) {
            setShowCancelConfirm(true);
        } else if (config.cancel?.yesUrl) {
            window.location.href = config.cancel.yesUrl;
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitted(true);
        if (!selectedCustomerId || !selectedType || !selectedCountry ||
            (selectedCustomerId !== NOSLA_VALUE && (selectedUserIds.length === 0 || selectedSiteIds.length === 0))) {
            return;
        }
        setSubmitting(true);
        const payload: SubscriptionPayload = {
            country: selectedCountry, customerId: selectedCustomerId,
            customerUserIds: selectedUserIds.map(Number), siteIds: selectedSiteIds, type: selectedType,
        };
        const isEdit = config.actionType?.toLowerCase() === 'edit' && !!subscriptionId;
        try {
            if (isEdit) { await updateSubscription(subscriptionId!, payload); }
            else { await addSubscription(payload); }
            ToastService.success(getSubmitSuccessMsg(isEdit, config), config.confirmation?.successTitle);
            setSelectedCustomerId('');
            setSelectedType('');
            setSelectedCountry('');
            setSelectedUserIds([]);
            setSelectedSiteIds([]);
            setSubmitted(false);
            if (config.confirmURL) { setTimeout(() => { window.location.href = config.confirmURL!; }, 3000); }
        } catch {
            ToastService.error(getSubmitErrorMsg(isEdit, config), errTitle);
        } finally {
            setSubmitting(false);
        }
    };

    return {
        showCancelConfirm, setShowCancelConfirm,
        customers, loadingInitial, loadingDependent, submitting,
        selectedCustomerId, setSelectedCustomerId,
        selectedType, setSelectedType,
        selectedCountry, setSelectedCountry,
        selectedUserIds, setSelectedUserIds,
        selectedSiteIds, setSelectedSiteIds,
        subscriptionTypeOptions, customerOptions, countryOptions, userOptions, siteOptions,
        isTypeInvalid, isCountryInvalid, isEmailInvalid, isSiteInvalid, isCustomerInvalid,
        showNoUsersMsg, showNoSitesMsg,
        handleCancelClick, handleSubmit,
        searchingCustomers, loadingMoreCustomers, hasMoreCustomers,
        setCustomerSearchTerm, loadMoreCustomers,
    };
}
